import React from 'react';
import {TaxModel} from '../../../../../domain/models/checkout/CartSummary/Tax.model';
import {ILocaleKeys} from '../../../../../locale-keys/LocaleKeys';
import {
  getTaxName,
  isFullTaxExempt,
  isPartialTaxExempt,
  formatSignedAmount,
} from '../../../../../domain/utils/total.util';
import {ExemptTax} from './ExemptTax';

export const getTaxIncludedWithPartialExemptionText = ({
  tax,
  localeKeys,
}: {
  tax: TaxModel;
  localeKeys: ILocaleKeys;
}): string =>
  localeKeys.checkout.order_summary.totals.includesTaxDisclaimer({
    taxName: getTaxName({
      taxName: tax.name,
      localeKeys,
    }),
    taxAmount: tax.amount.formattedAmount,
  });

export const getSingleTaxIncludedText = ({tax, localeKeys}: {tax: TaxModel; localeKeys: ILocaleKeys}): string => {
  if (isFullTaxExempt(tax)) {
    return localeKeys.checkout.order_summary.totals.includesTax.taxExemption.label();
  }

  return localeKeys.checkout.order_summary.totals.includesTaxDisclaimer({
    taxAmount: tax.amount.formattedAmount,
    taxName: getTaxName({
      taxName: tax.name,
      localeKeys,
    }),
  });
};

export const getTaxIncludedRowText = ({
  tax,
  localeKeys,
  hasOneTaxWithPartialExemption,
}: {
  tax: TaxModel;
  localeKeys: ILocaleKeys;
  hasOneTaxWithPartialExemption: boolean;
}): string => {
  if (hasOneTaxWithPartialExemption) {
    return getTaxIncludedWithPartialExemptionText({
      tax,
      localeKeys,
    });
  }

  if (isFullTaxExempt(tax)) {
    return localeKeys.checkout.order_summary.totals.includesTax.taxExemptionBreakdown.label({
      taxName: getTaxName({
        taxName: tax.name,
        localeKeys,
      }),
      taxAmount: tax.exemptAmount.formattedAmount,
    });
  }

  if (isPartialTaxExempt(tax)) {
    return localeKeys.checkout.order_summary.totals.includesTax.partialTaxExemptionBreakdown.label({
      taxName: getTaxName({
        taxName: tax.name,
        localeKeys,
      }),
      taxAmount: tax.amount.formattedAmount,
    });
  }

  return localeKeys.checkout.order_summary.totals.taxGroupsDisclaimer.breakdown({
    taxName: getTaxName({
      taxName: tax.name,
      localeKeys,
    }),
    taxAmount: tax.amount.formattedAmount,
  });
};

export const getTaxValue = ({
  tax,
  isTaxIncludedInPrice,
  isRTL,
  localeKeys,
}: {
  tax: TaxModel;
  isTaxIncludedInPrice: boolean;
  isRTL: boolean;
  localeKeys: ILocaleKeys;
}) => {
  if (isFullTaxExempt(tax)) {
    return isTaxIncludedInPrice
      ? formatSignedAmount({amount: tax.originalAmount.formattedAmount, isRTL})
      : localeKeys.checkout.order_summary.totals.fullTaxExemption.label();
  }

  if (isPartialTaxExempt(tax)) {
    return isTaxIncludedInPrice
      ? formatSignedAmount({amount: tax.exemptAmount.formattedAmount, isRTL})
      : () => <ExemptTax originalAmount={tax.originalAmount.formattedAmount} amount={tax.amount.formattedAmount} />;
  }
  return tax.amount.formattedAmount;
};

export const getTaxTitle = ({
  tax,
  localeKeys,
  addExemptTextToTaxNameWhenPartiallyExempt,
}: {
  tax: TaxModel;
  localeKeys: ILocaleKeys;
  addExemptTextToTaxNameWhenPartiallyExempt: boolean;
}): string => {
  const taxName = getTaxName({
    taxName: tax.name,
    localeKeys,
  });
  return addExemptTextToTaxNameWhenPartiallyExempt && isPartialTaxExempt(tax)
    ? localeKeys.checkout.order_summary.totals.partialTaxExemptionRow.label({taxName})
    : taxName;
};
